import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function GatesheadCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Gateshead Pothole Claims | 18% of Estate Roads Need Repair | Fixtyer"
        description="Gateshead Council earns GREEN on spend but AMBER overall — 18% of its 682km of unclassified roads need maintenance, with 16,334 pothole repairs in five years. See what the DfT rating means for your claim."
        canonical="/councils/gateshead"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Gateshead', url: '/councils/gateshead' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Gateshead has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spend. Gateshead\'s own published condition data shows 18% of its 682km unclassified network should be considered for maintenance (roughly 123km), while classified A and B/C roads sit at just 1–2%. The overall AMBER rating reflects that estate-road backlog, not a failure to spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 682.2km — 76% of Gateshead\'s 901.7km carriageway network. The council\'s transparency data publishes the percentage of each road class that should be considered for maintenance: 1% on A-roads, 2% on B/C roads, and 18% on U-roads in 2024/25 (down from a five-year peak of 24% in 2022/23). That is estate streets, back lanes and industrial estate routes — where most pothole claims happen.',
          },
          {
            question: 'The unclassified figure fell from 24% to 18% — does that weaken my claim?',
            answer: 'No — context matters. The council reports that for the first time in several years the unclassified backlog has started to reduce, yet 18% still equates to about 123km needing maintenance. Planned capital work in 2024/25 treated only 9.4km of U-roads — less than 10km of a 123km backlog — while almost 60% of planned spend went to that network. A downward trend does not mean your specific defect was reasonably known and repaired.',
          },
          {
            question: 'Does the council repairing 16,334 potholes in five years help or hurt my claim?',
            answer: 'It can help. Gateshead recorded 2,892 to 4,154 pothole repairs per year from 2020/21 to 2024/25 — 16,334 over five years — and admits its reactive maintenance budget regularly overspends after wet winters. A network producing thousands of pothole repairs annually is one where defects routinely form between inspections. Prior reports, photos showing defect age, and proof the pothole met the council\'s own 150mm × 40mm intervention threshold strengthen your case.',
          },
          {
            question: 'How does Section 58 apply to Gateshead\'s inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Gateshead inspects its highway and footpath network throughout the year under its Highway Safety Inspection Policy, with response targets from two hours for priority defects to 14 days for lower-risk carriageway repairs. The policy is developed with legal and insurance input because, as the council notes, detail on any claim can be challenged in court. Your claim succeeds when evidence shows the specific defect should have been found and fixed before you hit it.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Gateshead Council"
        councilSlug="gateshead"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/gateshead"
        headline="Gateshead Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Gateshead</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Gateshead: GREEN Spend, 123km of Estate Roads in the Backlog
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Gateshead earns <strong className="text-emerald-400">GREEN</strong> on the DfT spend scorecard and ranks
            its A and B/C roads in the highest condition quartile nationally. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">18% of its 682km unclassified network</strong> should be considered for
            maintenance, the council treated less than 10km of that backlog in 2024/25, and it still repaired{' '}
            <strong className="text-rose-400">16,334 potholes in five years</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18%</div>
            <div className="text-white font-medium mt-2">Unclassified roads needing maintenance (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 123km of estate roads, back lanes and industrial routes — on a network where U-roads are 76% of
              all carriageway and only 42% score GREEN on the DfT condition measure.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 902km Network Built for Through-Traffic
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Gateshead&apos;s own transparency report — where pothole claims actually happen
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Needing maintenance (2024/25)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A-roads</td>
                  <td className="px-6 py-4 text-center font-mono">78.1</td>
                  <td className="px-6 py-4 text-center">8.7%</td>
                  <td className="px-6 py-4 text-center text-emerald-700 font-medium">1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">141.4</td>
                  <td className="px-6 py-4 text-center">15.7%</td>
                  <td className="px-6 py-4 text-center text-emerald-700 font-medium">2%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-semibold text-amber-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">682.2</td>
                  <td className="px-6 py-4 text-center font-bold">75.6%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">901.7</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Beyond Carriageway</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 1,191km of footways</li>
                <li>• 289 bridges and highway structures</li>
                <li>• 170 traffic signalled junctions or crossings</li>
                <li>• 32,000 streetlights</li>
              </ul>
              <p className="text-surface-500 text-sm mt-4">
                National Highways maintains the A1 and A194(M) within the borough; everything else is Gateshead&apos;s
                Section 41 duty.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Cross-Boundary Pressure</h3>
              <p className="text-amber-800 text-sm">
                Gateshead sits inside the Tyneside conurbation. Commuter and through-traffic converges at a handful of
                Tyne crossings, adding wear on principal routes that also carry regional traffic to Newcastle&apos;s
                urban core. The council shares bridge maintenance with Newcastle City across the river.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Gateshead's position within the Tyneside conurbation means that, as well as catering for local traffic, there are significant amounts of travel through the area to and from adjoining areas. This results in additional pressures on the main cross boundary routes from commuter and other traffic. These are intensified by the limited number of river crossings, which means that traffic converges at a small number of points on the network."
            source="Gateshead Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of published survey data — classified roads flat at 1–2%, estate roads stuck near one in five
          </p>

          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">A and B/C roads (219.5km — 24% of network): stable</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">1%</div>
                <div className="text-sm text-emerald-800">A-roads needing maintenance</div>
                <div className="text-xs text-surface-500">unchanged every year since 2017/18</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">2%</div>
                <div className="text-sm text-emerald-800">B/C roads needing maintenance</div>
                <div className="text-xs text-surface-500">unchanged every year since 2017/18</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              Credit where due: principal and classified roads are genuinely well maintained. The DfT scores 92.5% of
              A-road length and 89.0% of B/C length in good condition. But classified roads are only a quarter of the
              network — and not where most residential pothole claims land.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (682km — 76% of network): the AMBER driver</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">% needing maintenance</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Approx. km affected</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">~136km</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">24%</td>
                    <td className="px-4 py-2 text-center font-bold">~164km</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">~143km</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">~143km</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">18%</td>
                    <td className="px-4 py-2 text-center font-bold">~123km</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council reports the unclassified backlog has started to reduce for the first time in several years —
              but 18% has hovered between 18% and 24% since 2017/18. On the DfT&apos;s wider condition measure, only{' '}
              <strong>42.0%</strong> of U-road length scores GREEN — meaning well over half the estate network is not in
              good condition by national benchmarking standards.
            </p>
          </div>

          <QuoteCard
            quote="Gateshead Council, as the highway authority, has a legal responsibility to maintain adopted highway in the borough (except the A1 and A194(M), which are maintained by National Highways)."
            source="Gateshead Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Backlog vs treatment */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            123km Backlog, 9.4km Treated
          </h2>
          <p className="text-surface-600 mb-8">
            Planned capital spend targets estate roads — but the maths still leaves most of the backlog untouched
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length treated (2024/25)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Budget share</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A-road</td>
                  <td className="px-6 py-4 text-center font-mono">1.8km</td>
                  <td className="px-6 py-4 text-center">£252,914</td>
                  <td className="px-6 py-4 text-center">8.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B-road</td>
                  <td className="px-6 py-4 text-center font-mono">1.4km</td>
                  <td className="px-6 py-4 text-center">£374,128</td>
                  <td className="px-6 py-4 text-center">12.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">C-road</td>
                  <td className="px-6 py-4 text-center font-mono">3.7km</td>
                  <td className="px-6 py-4 text-center">£575,647</td>
                  <td className="px-6 py-4 text-center">19.6%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-semibold text-amber-900">U-road</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">9.4km</td>
                  <td className="px-6 py-4 text-center font-bold">£1,731,608</td>
                  <td className="px-6 py-4 text-center font-bold">59.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">16.3km</td>
                  <td className="px-6 py-4 text-center font-bold">£2,934,297</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">The Backlog Arithmetic</h3>
              <p className="text-rose-800 text-sm mb-3">
                At 18% needing maintenance, roughly <strong>123km</strong> of unclassified carriageway requires
                attention. Planned capital work treated <strong>9.4km</strong> in 2024/25 — under 8% of the backlog in
                a single year, and the council itself notes this is still less than 10km of the 123km concern.
              </p>
              <p className="text-rose-800 text-sm">
                Historic underfunding on U-roads is flagged in the regional Transport Asset Management Plan and raised
                with the North East Combined Authority — neighbouring authorities report similar estate-road backlogs.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Forward Programme</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council&apos;s published 2025/26 maintenance scheme lists 21 principal unclassified resurfacing
                schemes (2025/MP/01–21) plus reserve schemes — from Chopwell and High Spen to Bensham and Team Valley
                — alongside classified-route works on the A184 Felling bypass and A1114 Metrocentre approaches.
              </p>
              <p className="text-surface-600 text-sm">
                A five-year Pothole Pro patching programme also began in 2025/26, targeting larger areas with a
                specialist machine procured through an internal funding bid.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Gateshead must show it had a reasonable system for knowing and fixing
              defects. On unclassified roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on the 2024/25 planned programme — or one of the 21 schemes for 2025/26?</li>
              <li>• If 123km needs maintenance, what inspection frequency applied to your street?</li>
              <li>• Did the defect meet the council&apos;s 150mm diameter × 40mm depth intervention threshold?</li>
              <li>• Was it reported before you hit it — and if so, which response-time category applied?</li>
            </ul>
            <p className="text-white font-medium">
              A council that treats under 10km of a 123km backlog cannot claim comprehensive knowledge of every
              estate-road defect.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            16,334 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council&apos;s published maintenance records
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes repaired</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">All defects</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive spend</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,892</td>
                  <td className="px-6 py-4 text-center">11,332</td>
                  <td className="px-6 py-4 text-center">£1,190,385</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,109</td>
                  <td className="px-6 py-4 text-center">13,007</td>
                  <td className="px-6 py-4 text-center">£1,418,426</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,940</td>
                  <td className="px-6 py-4 text-center">13,380</td>
                  <td className="px-6 py-4 text-center">£1,401,402</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,154</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16,359</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£1,809,016</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,239</td>
                  <td className="px-6 py-4 text-center">12,103</td>
                  <td className="px-6 py-4 text-center">£1,383,244</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16,334</td>
                  <td className="px-6 py-4 text-center font-bold">66,181</td>
                  <td className="px-6 py-4 text-center font-bold">£7,202,473</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~9 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Gateshead repairs around nine potholes per day — peaking at 4,154 in 2023/24
                after a wet preceding year. The council links repair volumes to winter severity and to the unclassified
                backlog. That is a network where defects form faster than planned resurfacing catches up.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive Budget Pressure</h3>
              <p className="text-amber-800 text-sm">
                Reactive maintenance spend hit £1.81m in 2023/24. The council states the annual reactive budget{' '}
                <strong>regularly overspends</strong>, particularly after difficult winter weather — and that the
                unclassified backlog is a contributory factor. Pothole Pro patching from 2025/26 is intended to reduce
                that pressure.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The Department for Transport requires all local highways authorities to publish information about their highways maintenance activities to help local taxpayers see the difference that funding is making in their areas."
            source="Gateshead Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend on a Split Network
          </h2>
          <p className="text-surface-600 mb-8">
            Why the DfT awards GREEN for spend — and why that does not automatically defeat your claim
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">£2.93m</div>
              <div className="text-sm text-emerald-800 mt-2">Planned capital maintenance (2024/25)</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">£1.8m</div>
              <div className="text-sm text-emerald-800 mt-2">Extra DfT capital uplift (2025/26)</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">59.1%</div>
              <div className="text-sm text-emerald-800 mt-2">2024/25 capital on U-roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Planned capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Combined</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£2,934,297</td>
                  <td className="px-4 py-3 text-center">£1,383,244</td>
                  <td className="px-4 py-3 text-center">£4,317,541</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">—</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">£1,809,016</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21 – 2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400 italic">See annual HAMP reports</td>
                  <td className="px-4 py-3 text-center">£1.19m – £1.42m/yr</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm mb-3">
                The DfT spend scorecard is GREEN with a capital spend score of 90.6 — reflecting investment above the
                baseline expectation. Gateshead receives highways maintenance funding through the North East Combined
                Authority devolution settlement, submitted its transparency report in June 2025, and secured a{' '}
                <strong>£1.8 million</strong> DfT uplift for 2025/26 (with £380,000 initially held back pending
                incentive compliance).
              </p>
              <p className="text-emerald-800 text-sm">
                The highway asset replacement value is assessed at <strong>£1.872 billion</strong>. Almost 60% of
                planned 2024/25 capital went to unclassified roads — the council is not ignoring estate streets in its
                budget allocation.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why Best Practice Is AMBER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Best practice scores AMBER despite innovation — micro-surfacing, thin surfacing, jet patching, warm-lay
                materials and the new Pothole Pro programme. The DfT&apos;s wider best-practice scorecard reflects
                treatment intensity on roads in amber/green condition (26.5) and red-road resurfacing pace (47.4) as
                well as preventative spend weighting (77.4).
              </p>
              <p className="text-amber-800 text-sm">
                The Highway Safety Inspection Policy was reviewed in 2025/26 with minor changes after a court case
                clarified link-road definitions — evidence the council knows policy detail is litigation-critical.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The total length of the highway network in Gateshead is approximately 560 miles (900km). The network reflects the mixed character of Gateshead, ranging from major urban routes carrying large volumes of traffic to more lightly used rural roads some over 200 metres in altitude providing access to outlying villages and farms."
            source="Gateshead Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Inspection */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Thresholds and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            What Gateshead says it does to find and fix defects — and the questions to ask on your road
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Inspection Regime</h3>
              <p className="text-surface-600 text-sm mb-3">
                Gateshead&apos;s highway and footpath network is inspected throughout the year under its Highway Safety
                Inspection Policy — developed with legal, insurance and frontline teams because policy detail on any
                claim can be challenged in court.
              </p>
              <p className="text-surface-600 text-sm">
                Inspections combine planned safety patrols and reactive checks. The council tracks pothole reports,
                deploys area highway inspectors, and uses gulley-management software across 30,000 highway drains to
                reduce flood-related road damage.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Intervention Thresholds</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Pothole defined as break or depression in bituminous surface</li>
                <li>• Minimum repairable defect: <strong>150mm diameter × 40mm depth</strong></li>
                <li>• Priority (R0): make safe within <strong>2 hours</strong></li>
                <li>• R1: within <strong>24 hours</strong></li>
                <li>• R2: within <strong>14 days</strong></li>
                <li>• R3 low-risk: review after <strong>12 months</strong></li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Building Your Section 41 / Section 58 Case</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Gateshead must maintain its adopted network. Section 58 gives
              the council a defence only where it proves reasonable systems were in place. Against a GREEN-spend,
              AMBER-condition authority, your evidence should establish:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• The defect exceeded 150mm × 40mm — photos with a ruler or known object for scale</li>
              <li>• Prior reports on the same defect (FixMyStreet, council online forms) proving actual notice</li>
              <li>• Visible age: weathered edges, previous patching, surrounding fatigue cracking</li>
              <li>• Road class — on a U-road in the 123km backlog, the treatment gap is your structural argument</li>
              <li>• Whether response-time targets for the defect category were missed before your incident</li>
            </ul>
            <p className="text-white font-medium">
              Mac searches for prior reports, assesses your photos against Gateshead&apos;s own intervention criteria,
              and cites the council&apos;s published condition and repair data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Gateshead is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests through NECA and above DfT baseline expectations
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C roads at 1–2% needing maintenance, highest national quartile
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 59.1% of 2024/25 planned capital directed at U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented inspection policy reviewed with legal input; gulley and report-tracking systems
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Unclassified backlog reducing — 18% down from 24% peak
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on principal roads. Generic claims on A184 or A1114 corridors will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — only 42.0% of U-road length in GREEN on DfT measures
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 123km of estate roads needing maintenance — 9.4km treated in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16,334 pothole repairs in five years — defects outpace planned works
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive budget regularly overspends after wet winters
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — treatment and resurfacing scores below top tier
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and improving backlog trends, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice, not theoretical inspection</li>
              <li>• Photos showing size above 150mm × 40mm and visible age</li>
              <li>• Estate-road location — 76% of the network, 18% in the maintenance backlog</li>
              <li>• Whether your road appears on the published 2025/26 maintenance scheme programme</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment against Gateshead&apos;s own thresholds,
              and citation of the council&apos;s transparency data where it helps you — without pretending the
              authority is failing on every front.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Gateshead?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 123km backlog argument</li>
                <li>• No 150mm × 40mm threshold analysis</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 18% U-road backlog documented</li>
                <li>✅ 16,334 repairs in five years cited</li>
                <li>✅ Intervention thresholds and response times argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Gateshead</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=gateshead"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Gateshead Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Gateshead has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spend. Gateshead&apos;s
                own published condition data shows 18% of its 682km unclassified network should be considered for maintenance
                (roughly 123km), while classified A and B/C roads sit at just 1–2%. The overall AMBER rating reflects that
                estate-road backlog, not a failure to spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 682.2km — 76% of Gateshead&apos;s 901.7km carriageway network. The council&apos;s
                transparency data publishes the percentage of each road class that should be considered for maintenance: 1%
                on A-roads, 2% on B/C roads, and 18% on U-roads in 2024/25 (down from a five-year peak of 24% in 2022/23).
                That is estate streets, back lanes and industrial estate routes — where most pothole claims happen.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The unclassified figure fell from 24% to 18% — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — context matters. The council reports that for the first time in several years the unclassified backlog
                has started to reduce, yet 18% still equates to about 123km needing maintenance. Planned capital work in
                2024/25 treated only 9.4km of U-roads — less than 10km of a 123km backlog — while almost 60% of planned
                spend went to that network. A downward trend does not mean your specific defect was reasonably known and repaired.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the council repairing 16,334 potholes in five years help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can help. Gateshead recorded 2,892 to 4,154 pothole repairs per year from 2020/21 to 2024/25 — 16,334
                over five years — and admits its reactive maintenance budget regularly overspends after wet winters. A network
                producing thousands of pothole repairs annually is one where defects routinely form between inspections. Prior
                reports, photos showing defect age, and proof the pothole met the council&apos;s own 150mm × 40mm intervention
                threshold strengthen your case.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Gateshead&apos;s inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems.
                Gateshead inspects its highway and footpath network throughout the year under its Highway Safety Inspection
                Policy, with response targets from two hours for priority defects to 14 days for lower-risk carriageway
                repairs. The policy is developed with legal and insurance input because, as the council notes, detail on any
                claim can be challenged in court. Your claim succeeds when evidence shows the specific defect should have been
                found and fixed before you hit it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.gateshead.gov.uk/article/33237/Local-Highway-Maintenance-Transparency-Report-2025-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Gateshead Council Local Highways Maintenance Transparency Report 2025–2026
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
